// User-facing action helpers: wrap the mutator with the client-side rules
// (one active task per device, the "Estimated · Took" flash) and error toasts.
import { engine, mutate } from '@/runtime'
import { db } from '@/runtime'
import { KindError } from '@/kinds/kind'
import type { TaskResource } from '@/kinds/task/apply'
import { useUi } from '@/state/ui'
import { undoWithFlash } from '@/lib/undo'
import { tick } from '@/lib/haptics'
import { fmtMinutes, minutesBetween, nowISO } from './time'

const toast = (msg: string) => useUi.getState().showFlash(msg, 3000)

const guard = async (fn: () => Promise<void>) => {
  try {
    await fn()
  } catch (e) {
    if (e instanceof KindError) toast(e.message)
    else throw e
  }
}

const tookText = (t: TaskResource, at: string) => {
  let minutes = t.status.actual_minutes
  if (t.status.started_at) minutes += minutesBetween(t.status.started_at, at)
  const est = t.spec.estimate_minutes
  if (est && minutes) return `Estimated ${fmtMinutes(est)} · Took ${fmtMinutes(minutes)}`
  if (minutes) return `Took ${fmtMinutes(minutes)}`
  if (est) return `Done · estimated ${fmtMinutes(est)}`
  return 'Done'
}

export const taskActions = {
  complete: (t: TaskResource) =>
    guard(async () => {
      const at = nowISO()
      await mutate.action(t.id, 'complete')
      const ui = useUi.getState()
      if (ui.activeTaskId === t.id) ui.setActiveTask(null)
      ui.showFlash(tookText(t, at))
    }),
  /** A house job done for now: its clock restarts and it stays open; one tap, with Undo. */
  doneNow: (t: TaskResource) =>
    guard(async () => {
      tick()
      await mutate.action(t.id, 'done_now')
      useUi.getState().showFlash('Done for now', 5000, { label: 'Undo', run: () => void undoWithFlash() })
    }),
  start: (t: TaskResource) =>
    guard(async () => {
      const ui = useUi.getState()
      if (ui.activeTaskId && ui.activeTaskId !== t.id) {
        const other = (await db.resources.get(ui.activeTaskId)) as TaskResource | undefined
        if (other?.status.started_at && !other.deleted_at) await mutate.action(other.id, 'stop')
      }
      if (!t.status.started_at) await mutate.action(t.id, 'start')
      ui.setActiveTask(t.id)
    }),
  stop: (t: TaskResource) =>
    guard(async () => {
      if (t.status.started_at) await mutate.action(t.id, 'stop')
      const ui = useUi.getState()
      if (ui.activeTaskId === t.id) ui.setActiveTask(null)
    }),
  toggleTimer: (t: TaskResource) => (t.status.started_at ? taskActions.stop(t) : taskActions.start(t)),
  defer: (t: TaskResource, scheduled: string, scheduled_time?: string) =>
    guard(() =>
      mutate.action(
        t.id,
        'defer',
        scheduled_time !== undefined ? { scheduled, scheduled_time } : { scheduled },
      ),
    ),
  move: (t: TaskResource, state: 'inbox' | 'next' | 'waiting' | 'someday') =>
    guard(() => mutate.action(t.id, 'move', { state })),
  drop: (t: TaskResource, reason?: string) =>
    guard(async () => {
      await mutate.action(t.id, 'drop', reason ? { reason } : {})
      const ui = useUi.getState()
      if (ui.activeTaskId === t.id) ui.setActiveTask(null)
    }),
  reopen: (t: TaskResource) => guard(() => mutate.action(t.id, 'reopen')),
  touch: (t: TaskResource) => guard(() => mutate.action(t.id, 'touch')),
  setPriority: (t: TaskResource, priority: number) => guard(() => mutate.patch(t.id, { spec: { priority } })),
  setProject: (t: TaskResource, projectId: string | null) => guard(() => mutate.setProject(t.id, projectId)),
  remove: (t: TaskResource) => guard(() => deleteWithUndo(t.id, t.spec.title)),
}

/**
 * Delete something and offer to take it back. Nothing is really gone: the
 * resource is soft-deleted and sits in the Trash until it is purged.
 */
export const deleteWithUndo = async (id: string, name: string) => {
  await mutate.remove(id)
  useUi.getState().showFlash(`Deleted “${name}”`, 6000, { label: 'Undo', run: () => void undoWithFlash() })
}

export const retryOp = (opId: string, discard: boolean) => engine.retryOp(opId, discard)
